import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';

import PerformanceBoardSettingsContext from '../../contexts/PerformanceBoardSettingsContext';
import useGetDatasetLabel from '../../../../Inputs/MetricPicker/MetricPickerPopup/hooks/useGetDatasetLabel';

import GridColumnSizeSettings from '../../../../Inputs/GridColumnSizeSettings';
import AccountPickerContext from '../../../../../contexts/AccountPickerContext';
import STORE from '../../../../../store';
import withoutNulls from '../../../../../api/search/withoutNulls';
import getTimeStamp from '../../../../../getTimeStamp';
import BoardSettingsHeader from '../BoardSettingsHeader/BoardSettingsHeader';
import Row from '../../../../Common/Row';
import Button from '../../../../../kingpin/atoms/Button';
import useColumnOrder from '../hooks/useColumnOrder';
import useGetFieldLabel from '../../../../../hooks/useGetFieldLabel';

const useFields = () => {
  const { board } = useContext(PerformanceBoardSettingsContext);
  const { columnOrder } = useColumnOrder();
  const { getFieldLabel } = useGetFieldLabel();
  const fields = useMemo((): {
    key: string;
    field: string;
    label: string;
  }[] => {
    return columnOrder
      .filter((c) => c.visible)
      .map((c) => {
        const isProgressField = c.type === 'progress';

        return {
          key: `${c.field}-${c.type}`,
          field: c.field,
          label: `${getFieldLabel({
            field: c.field,
            dataType: board.dataType,
          })}${isProgressField ? ' (Progress Field)' : ''}`,
        };
      });
  }, [board.dataType, columnOrder, getFieldLabel]);

  return fields;
};

const BoardColumnSizeSettingsContainer = () => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { board, hasUnsavedChanges, setHasUnsavedChanges } = useContext(
    PerformanceBoardSettingsContext,
  );
  const [columnSizeStrategy, setColumnSizeStrategy] = useState<
    FleetOps.ColumnSizeStrategies.Strategy | undefined
  >(board.columnSizeStrategy);

  const fields = useFields();
  const getDatasetLabel = useGetDatasetLabel();

  useEffect(() => {
    setHasUnsavedChanges(
      !_.isEqual(columnSizeStrategy, board.columnSizeStrategy),
    );
  }, [board.columnSizeStrategy, columnSizeStrategy, setHasUnsavedChanges]);

  const onSave = useCallback(() => {
    STORE.contentDistributions
      .getPerformanceBoardsRef({
        accountId: selectedAccountId,
      })
      .doc(board.id)
      .set({
        ...board,
        columnSizeStrategy: columnSizeStrategy
          ? withoutNulls(columnSizeStrategy)
          : undefined,
        updatedOn: getTimeStamp(),
      });
  }, [board, columnSizeStrategy, selectedAccountId]);

  const label = (
    <BoardSettingsHeader
      title="Column Size Settings"
      description={`Dataset: ${getDatasetLabel(board.dataType)}`}
    />
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <GridColumnSizeSettings
        label={label}
        fields={fields}
        columnSizeStrategy={columnSizeStrategy}
        setColumnSizeStrategy={setColumnSizeStrategy}
      />
      <Row
        style={{ padding: '8px 0', height: '46px', justifyContent: 'flex-end' }}
      >
        <Button
          type="Primary"
          size="Small"
          isDisabled={!hasUnsavedChanges}
          onClick={onSave}
          label="Save"
        />
      </Row>
    </div>
  );
};

export default BoardColumnSizeSettingsContainer;
